import { type CSSProperties } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import { type ClipSpriteSheet } from '@air/types';
import { getClipSpritesheetAspectRatio, getClipSpritesheetFrameCount } from '@air/utils-clips';

export const SCRUBBABLE_THUMBNAIL_INDICATOR_WIDTH = 2;
export type Dimension = {
  height: number;
  width: number;
};

const isHorizontal = (aspectRatio: number) => aspectRatio > 1;

export const getDimensions = (aspectRatio: number, width: number, height: number) => {
  if (isHorizontal(aspectRatio)) {
    const scaledHeight = height;
    const scaledWidth = scaledHeight * aspectRatio;

    if (scaledWidth < width) {
      return {
        aspectRatio,
        scaledHeight: width / aspectRatio,
        scaledWidth: width,
      };
    }

    return {
      aspectRatio,
      scaledHeight,
      scaledWidth,
    };
  }

  const scaledWidth = width;
  const scaledHeight = scaledWidth * aspectRatio;

  if (scaledHeight > height) {
    return {
      aspectRatio,
      scaledHeight: height,
      scaledWidth: height * aspectRatio,
    };
  }

  return {
    aspectRatio,
    scaledHeight: scaledWidth / aspectRatio,
    scaledWidth,
  };
};

export const clipSupportsStaticThumbnail = (clip: Clip) =>
  !!clip.assets.spriteSheet?.src && clip.type === ClipType.video && Number.isInteger(clip.position?.start.thumbnail);

/**
 * Calculates all sprite positioning values for a given frame.
 * Contains the shared logic between getSpriteStyles and getBackgroundPositionFromFrame.
 */
const calculateSpritePosition = ({
  containerWidth,
  containerHeight,
  frame,
  spritesheet,
}: {
  containerWidth: number;
  containerHeight: number;
  frame: number;
  spritesheet: ClipSpriteSheet;
}) => {
  const frameCount = getClipSpritesheetFrameCount(spritesheet);
  const aspectRatio = getClipSpritesheetAspectRatio(spritesheet);
  const dimensions = getDimensions(aspectRatio, containerWidth, containerHeight);

  const frameWidth = Math.floor(dimensions.scaledWidth);
  const frameHeight = Math.floor(dimensions.scaledHeight);

  const currentFrame = frame === frameCount ? frameCount - 1 : frame;
  const column = currentFrame % spritesheet.columns;
  const row = Math.floor(currentFrame / spritesheet.columns);

  const spriteSheetWidth = frameWidth * spritesheet.columns;
  const spriteSheetHeight = frameHeight * spritesheet.rows;
  const backgroundPositionX = -(column * frameWidth);
  const backgroundPositionY = -(row * frameHeight);

  return {
    frameWidth,
    frameHeight,
    spriteSheetWidth,
    spriteSheetHeight,
    backgroundPositionX: Math.abs(backgroundPositionX) === 0 ? 0 : backgroundPositionX,
    backgroundPositionY: Math.abs(backgroundPositionY) === 0 ? 0 : backgroundPositionY,
    containerWidth,
    containerHeight,
    aspectRatio,
  };
};

/**
 * Calculates integer-pixel sprite positioning to prevent pixel bleeding.
 * Uses floor operations to ensure all calculations result in whole pixel values.
 */
export const getSpriteStyles = ({
  containerWidth,
  containerHeight,
  spritesheet,
  frame,
}: {
  containerWidth: number;
  containerHeight: number;
  spritesheet: ClipSpriteSheet;
  frame: number;
}): CSSProperties => {
  const { frameWidth, frameHeight, spriteSheetWidth, spriteSheetHeight, backgroundPositionX, backgroundPositionY } =
    calculateSpritePosition({ containerWidth, containerHeight, spritesheet, frame });
  const aspectRatio = getClipSpritesheetAspectRatio(spritesheet);

  return {
    width: frameWidth,
    height: frameHeight,
    backgroundSize: `${spriteSheetWidth}px ${spriteSheetHeight}px`,
    backgroundPosition: `${backgroundPositionX}px ${backgroundPositionY}px`,
    backgroundRepeat: 'no-repeat',
    transform:
      aspectRatio > 1
        ? `translateX(${Math.floor((containerWidth - frameWidth) / 2)}px)`
        : `translateY(${Math.floor((containerHeight - frameHeight) / 2)}px)`,
  };
};

/**
 * Gets integer pixel background position for a specific sprite frame.
 * Used for dynamic updates in scrubbable thumbnails.
 */
export const getBackgroundPositionFromFrame = ({
  containerWidth,
  containerHeight,
  spritesheet,
  frame,
}: {
  containerWidth: number;
  containerHeight: number;
  spritesheet: ClipSpriteSheet;
  frame: number;
}) => {
  const { backgroundPositionX, backgroundPositionY } = calculateSpritePosition({
    containerWidth,
    containerHeight,
    spritesheet,
    frame,
  });

  return {
    x: backgroundPositionX,
    y: backgroundPositionY,
  };
};
